import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { first, map } from 'rxjs';
import { LoggedUserService } from '../services/logged-user.service';
import { AccountDeactivatedService } from '../services/account-deactivated.service';

/**
 * Mid-session guard: redirect authenticated users whose status became
 * INACTIVE to the dedicated account-deactivated page. Applied to the
 * main app routes so a user deactivated server-side can never reach
 * the application after the next navigation.
 */
export const accountDeactivatedRedirectGuard: CanActivateFn = () => {
  const loggedUser = inject(LoggedUserService);
  const deactivated = inject(AccountDeactivatedService);
  const router = inject(Router);

  return loggedUser.user$.pipe(
    first((u) => u !== null),
    map((u) => {
      if (u?.status === 'INACTIVE') {
        deactivated.signaled = true;
        return router.parseUrl('/account-deactivated');
      }
      return true;
    }),
  );
};

/**
 * Page guard: only allows access to /account-deactivated when the user
 * has actually been routed there (an INACTIVE authenticated user or a
 * failed sign-in due to a disabled Cognito account). Direct URL access
 * is denied to keep the page from being a generic "logged-out landing".
 */
export const accountDeactivatedPageGuard: CanActivateFn = () => {
  const deactivated = inject(AccountDeactivatedService);
  const router = inject(Router);
  return deactivated.signaled ? true : router.parseUrl('/');
};
